import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as eqpProgramApi from '../../lib/api/eqpProgram';

const LINE_COLOR = {
  ADD: { bg: 'rgba(46, 160, 67, 0.15)', prefix: '+' },
  DEL: { bg: 'rgba(248, 81, 73, 0.15)', prefix: '-' },
  CTX: { bg: 'transparent', prefix: ' ' },
};

/**
 * (A) 가공 프로그램 버전 비교 - 모노스페이스 diff 뷰어. 폰에서도 보이게 가로 스크롤만 별도 컨테이너로 둔다.
 */
const EqpProgramDiffDialog = ({ open, eqpUuid, fileName, fromVerId, toVerId, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [diff, setDiff] = useState(null); // {truncated, reason, lines}

  useEffect(() => {
    if (!open || !eqpUuid || !fileName || !fromVerId || !toVerId) return;
    setLoading(true);
    setDiff(null);
    eqpProgramApi.getProgramDiff(eqpUuid, fileName, fromVerId, toVerId)
      .then((res) => setDiff(eqpProgramApi.extractInfo(res)))
      .catch(() => enqueueSnackbar('비교에 실패했습니다.', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [open, eqpUuid, fileName, fromVerId, toVerId, enqueueSnackbar]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{fileName} - 이전 버전과 비교</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        )}
        {!loading && diff?.truncated && (
          <Alert severity="warning">{diff.reason || '파일이 너무 커서 비교할 수 없습니다.'}</Alert>
        )}
        {!loading && diff && !diff.truncated && (
          <Box
            sx={{
              overflowX: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.6 }}>
              {(diff.lines || []).map((line, idx) => {
                const style = LINE_COLOR[line.type] || LINE_COLOR.CTX;
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex', bgcolor: style.bg, px: 1, whiteSpace: 'pre',
                    }}
                  >
                    <Typography component="span" sx={{ width: 56, flexShrink: 0, color: 'text.disabled', fontFamily: 'monospace', fontSize: 'inherit' }}>
                      {line.oldNo ?? ''}
                    </Typography>
                    <Typography component="span" sx={{ width: 56, flexShrink: 0, color: 'text.disabled', fontFamily: 'monospace', fontSize: 'inherit' }}>
                      {line.newNo ?? ''}
                    </Typography>
                    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
                      {style.prefix} {line.text}
                    </Typography>
                  </Box>
                );
              })}
              {(diff.lines || []).length === 0 && (
                <Box sx={{ px: 1, color: 'text.secondary' }}>차이가 없습니다.</Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EqpProgramDiffDialog;
